import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowsClockwise, Check, Link as LinkIcon, Trash, ShoppingCart, Warning } from '@phosphor-icons/react';

export default function ShopifyTab() {
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [appModels, setAppModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Mapping form
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedVariant, setSelectedVariant] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statusRes, mappingsRes, modelsRes] = await Promise.all([
        axios.get(`${API}/shopify/status`),
        axios.get(`${API}/shopify/mappings`),
        axios.get(`${API}/batch-models`)
      ]);
      setStatus(statusRes.data);
      setMappings(mappingsRes.data);
      setAppModels(modelsRes.data.filter(m => m.active !== false));

      if (statusRes.data.connected) {
        try {
          const productsRes = await axios.get(`${API}/shopify/products`);
          setProducts(productsRes.data);
        } catch (e) {
          if (e.response?.status === 400) {
            // Not connected
          } else {
            toast.error("Erreur chargement produits Shopify");
          }
        }
      }
    } catch (e) { console.error('Shopify sync error:', e.message); }
    finally { setLoading(false); }
  };

  const [refreshing, setRefreshing] = useState(false);

  const handleRefreshToken = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/shopify/refresh-token`);
      toast.success(`Token rafraîchi ! Expire: ${new Date(res.data.expires_at).toLocaleString('fr-FR')}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du rafraîchissement");
    } finally {
      setRefreshing(false);
    }
  };

  const handleConnect = async () => {
    try {
      const res = await axios.get(`${API}/shopify/auth`);
      window.open(res.data.auth_url, '_blank');
    } catch (e) {
      toast.error("Erreur lors de la connexion Shopify");
    }
  };

  const handleSaveMapping = async () => {
    if (!selectedModel || !selectedVariant) {
      toast.error("Sélectionnez un modèle et une variante");
      return;
    }

    // Find variant details
    let variantInfo = null;
    let productTitle = '';
    for (const p of products) {
      const v = p.variants.find(v => String(v.variant_id) === selectedVariant);
      if (v) {
        variantInfo = v;
        productTitle = p.title;
        break;
      }
    }

    if (!variantInfo) return;

    try {
      await axios.post(`${API}/shopify/mappings`, {
        model_name: selectedModel,
        variant_id: variantInfo.variant_id,
        inventory_item_id: variantInfo.inventory_item_id,
        product_title: productTitle,
        variant_title: variantInfo.title
      });
      toast.success(`Mapping sauvegardé: ${selectedModel} → ${productTitle} / ${variantInfo.title}`);
      setSelectedModel('');
      setSelectedVariant('');
      const r = await axios.get(`${API}/shopify/mappings`);
      setMappings(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur sauvegarde mapping");
    }
  };

  const handleDeleteMapping = async (modelName) => {
    try {
      await axios.delete(`${API}/shopify/mappings/${modelName}`);
      toast.success(`Mapping supprimé: ${modelName}`);
      setMappings(prev => prev.filter(m => m.model_name !== modelName));
    } catch (e) {
      toast.error("Erreur suppression");
    }
  };

  // Build flat list of all variants for selection
  const allVariants = products.flatMap(p =>
    p.variants.map(v => ({
      ...v,
      product_title: p.title,
      label: `${p.title} — ${v.title} (SKU: ${v.sku || '—'}) | Stock: ${v.inventory_quantity}`
    }))
  );

  // Models not yet mapped
  const mappedModels = mappings.map(m => m.model_name);
  const unmappedModels = appModels.filter(m => !mappedModels.includes(m.name));

  if (loading) {
    return <div className="flex items-center justify-center p-12 text-zinc-500">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="shopify-tab">
      {/* Connection Status */}
      <div className={`p-4 border rounded-sm ${status?.connected ? (status?.is_expired ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200') : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingCart size={24} className={status?.connected ? (status?.is_expired ? 'text-rose-600' : 'text-emerald-600') : 'text-amber-600'} weight="duotone" />
            <div>
              <p className="font-medium text-sm">
                {status?.connected ? (status?.is_expired ? `Token expiré — ${status.shop}` : `Connecté à ${status.shop}`) : 'Shopify non connecté'}
              </p>
              {status?.connected && (
                <p className="text-xs text-zinc-500">
                  {status.expires_at && `Expire le ${new Date(status.expires_at).toLocaleString('fr-FR')} — `}
                  Rafraîchissement auto toutes les 23h
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {status?.connected && (
              <Button onClick={handleRefreshToken} disabled={refreshing} variant="outline" size="sm" className="rounded-sm" data-testid="shopify-refresh-token-btn">
                <ArrowsClockwise size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Rafraîchissement...' : 'Rafraîchir le token'}
              </Button>
            )}
            <Button
              onClick={status?.connected ? fetchAll : handleConnect}
              variant={status?.connected ? 'outline' : 'default'}
              size="sm"
              className="rounded-sm"
              data-testid="shopify-connect-btn"
            >
              {status?.connected ? <><ArrowsClockwise size={14} className="mr-2" />Recharger</> : <><LinkIcon size={14} className="mr-2" />Connecter Shopify</>}
            </Button>
          </div>
        </div>
      </div>

      {!status?.connected && (
        <div className="bg-white border border-zinc-200 p-6 rounded-sm">
          <h3 className="font-heading font-bold text-lg mb-2">Comment connecter Shopify</h3>
          <ol className="text-sm text-zinc-600 space-y-2 list-decimal list-inside">
            <li>Cliquez sur <strong>"Connecter Shopify"</strong> ci-dessus</li>
            <li>Autorisez l'application sur Shopify</li>
            <li>Vous serez redirigé ici automatiquement</li>
            <li>Configurez le mapping entre vos modèles et les produits Shopify</li>
          </ol>
        </div>
      )}

      {status?.connected && (
        <>
          {/* Mapping Section */}
          <div className="bg-white border border-zinc-200 p-4 rounded-sm">
            <h3 className="font-heading font-bold text-base mb-4">Mapping Modèles → Produits Shopify</h3>

            {/* Add mapping form */}
            {unmappedModels.length > 0 && products.length > 0 && (
              <div className="flex flex-wrap gap-3 items-end mb-4 p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
                <div className="flex-1 min-w-[150px]">
                  <label className="text-xs text-zinc-500 mb-1 block">Modèle de lot</label>
                  <Select value={selectedModel} onValueChange={setSelectedModel}>
                    <SelectTrigger className="rounded-sm h-9 bg-white" data-testid="mapping-model-select">
                      <SelectValue placeholder="Sélectionner un modèle..." />
                    </SelectTrigger>
                    <SelectContent>
                      {unmappedModels.map(m => (
                        <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-[2] min-w-[250px]">
                  <label className="text-xs text-zinc-500 mb-1 block">Variante Shopify</label>
                  <Select value={selectedVariant} onValueChange={setSelectedVariant}>
                    <SelectTrigger className="rounded-sm h-9 bg-white" data-testid="mapping-variant-select">
                      <SelectValue placeholder="Sélectionner une variante..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allVariants.map(v => (
                        <SelectItem key={v.variant_id} value={String(v.variant_id)}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleSaveMapping} size="sm" className="rounded-sm bg-zinc-900 hover:bg-zinc-800 h-9" data-testid="save-mapping-btn">
                  <Check size={14} className="mr-1" />Enregistrer
                </Button>
              </div>
            )}

            {/* Existing mappings */}
            {mappings.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Modèle</TableHead>
                    <TableHead>Produit Shopify</TableHead>
                    <TableHead>Variante</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mappings.map(m => (
                    <TableRow key={m.model_name}>
                      <TableCell className="font-medium">{m.model_name}</TableCell>
                      <TableCell>{m.product_title}</TableCell>
                      <TableCell>{m.variant_title}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteMapping(m.model_name)} className="text-rose-500 hover:text-rose-700 h-7 w-7 p-0" data-testid={`delete-mapping-${m.model_name}`}>
                          <Trash size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-zinc-500 text-center py-4">Aucun mapping configuré. Ajoutez des correspondances ci-dessus.</p>
            )}
          </div>

          {/* Products Overview */}
          <div className="bg-white border border-zinc-200 p-4 rounded-sm">
            <h3 className="font-heading font-bold text-base mb-4">Produits Shopify ({products.length})</h3>
            {products.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produit</TableHead>
                    <TableHead>Variantes</TableHead>
                    <TableHead className="text-right">Stock total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map(p => (
                    <TableRow key={p.product_id}>
                      <TableCell className="font-medium">{p.title}</TableCell>
                      <TableCell className="text-sm text-zinc-500">{p.variants.map(v => v.title).join(', ')}</TableCell>
                      <TableCell className="text-right font-mono">{p.variants.reduce((s, v) => s + (v.inventory_quantity || 0), 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-zinc-500 text-center py-4">Aucun produit trouvé sur Shopify.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
